import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useAuth } from '@/auth/AuthContext'
import { THEMES, useTheme } from '@/lib/theme'

import { Wordmark } from './Masthead'
import { BureauChip, StatusDot } from './primitives'

interface Props {
  children: ReactNode
  bureau?: { host: string; role?: string } | null
  onOpenRolodex?: () => void
  onOpenSessions?: () => void
  compactMasthead?: boolean
  topBarRight?: ReactNode
  menuExtras?: ReactNode
  modelLabel?: string
  fallback?: boolean
}

/**
 * Hairline-framed terminal shell. Top bar with wordmark + host chip +
 * quick actions. Footer with a small status line.
 */
export function AppShell({
  children,
  bureau,
  onOpenRolodex,
  onOpenSessions,
  topBarRight,
  menuExtras,
  modelLabel,
  fallback,
}: Props) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [themePickerOpen, setThemePickerOpen] = useState(false)
  const [theme, setTheme] = useTheme()
  const activeTheme = THEMES.find((t) => t.id === theme) ?? THEMES[0]

  useEffect(() => {
    if (!menuOpen) return
    const onDoc = () => {
      setMenuOpen(false)
      setThemePickerOpen(false)
    }
    window.addEventListener('click', onDoc)
    return () => window.removeEventListener('click', onDoc)
  }, [menuOpen])

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <header className="border-b border-line bg-bg-raise shrink-0">
        <div className="flex items-center justify-between gap-4 px-3 py-2">
          <div className="flex items-center gap-3 min-w-0">
            {onOpenSessions && (
              <button
                onClick={onOpenSessions}
                className="btn btn-ghost min-[1200px]:hidden"
                aria-label="Open sessions"
              >
                <span className="text-fg-dim">[</span>
                <span>≡</span>
                <span className="text-fg-dim">]</span>
              </button>
            )}
            <Link to="/">
              <Wordmark size="md" />
            </Link>
            {bureau && (
              <span className="hidden min-[750px]:inline-flex items-center gap-3">
                <span aria-hidden className="text-fg-dim">·</span>
                <BureauChip host={bureau.host} role={bureau.role} onClick={onOpenRolodex} />
              </span>
            )}
            {modelLabel && (
              <span className="hidden min-[1200px]:inline-flex items-center gap-1.5 text-[12px] text-fg-dim ml-2">
                <span className="uppercase tracking-[0.08em]">model</span>
                <span className="text-fg-mute">{modelLabel}</span>
                {fallback && <span className="text-warn">(fallback)</span>}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            {topBarRight}
            {onOpenRolodex && (
              <button
                onClick={onOpenRolodex}
                className="btn btn-ghost hidden min-[1200px]:inline-flex"
                aria-label="Open search"
              >
                <span className="text-fg-dim">[</span>
                <span>⌘K</span>
                <span className="text-fg-dim">]</span>
                <span className="ml-0.5">search</span>
              </button>
            )}
            {user?.role === 'admin' && (
              <Link to="/admin" className="btn btn-ghost hidden min-[1200px]:inline-flex">
                <span className="text-fg-dim">[</span>
                <span>admin</span>
                <span className="text-fg-dim">]</span>
              </Link>
            )}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen((o) => !o)
                }}
                className="btn"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="User menu"
              >
                <span className="text-fg-dim">[</span>
                <StatusDot tone="accent" size={7} />
                <span className="hidden min-[1200px]:inline text-fg-mute">
                  {user?.role === 'admin' ? 'admin' : 'user'}
                </span>
                <span className="hidden min-[1200px]:inline text-fg truncate max-w-[140px]">
                  {user?.email}
                </span>
                <span className="text-fg-dim">▾]</span>
              </button>
              {menuOpen && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="absolute right-0 top-full mt-1 w-60 panel z-30 animate-fade-in"
                >
                  <div className="px-3 py-2 border-b border-line">
                    <div className="uppercase tracking-[0.08em] text-[11px] text-fg-dim">
                      signed in
                    </div>
                    <div className="mt-0.5 text-[13px] text-fg truncate">
                      {user?.email}
                    </div>
                    <div className="mt-0.5 text-[12px] text-fg-mute">
                      role: {user?.role === 'admin' ? 'admin' : 'user'}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setMenuOpen(false)
                      navigate('/')
                    }}
                    className="w-full text-left px-3 py-2 text-[14px] text-fg-soft hover:bg-surface hover:text-fg transition-colors"
                  >
                    <span className="text-accent mr-2">&gt;</span>
                    chat
                  </button>
                  {user?.role === 'admin' && (
                    <button
                      onClick={() => {
                        setMenuOpen(false)
                        navigate('/admin')
                      }}
                      className="w-full text-left px-3 py-2 text-[14px] text-fg-soft hover:bg-surface hover:text-fg transition-colors min-[1200px]:hidden"
                    >
                      <span className="text-accent mr-2">&gt;</span>
                      admin
                    </button>
                  )}
                  <div className="relative border-t border-line">
                    <button
                      onClick={() => setThemePickerOpen((o) => !o)}
                      className="w-full text-left px-3 py-2 text-[14px] text-fg-soft hover:bg-surface hover:text-fg transition-colors flex items-center"
                      aria-haspopup="menu"
                      aria-expanded={themePickerOpen}
                    >
                      <span className="text-accent mr-2">&gt;</span>
                      theme: <span className="text-fg ml-1">{activeTheme.label}</span>
                      <span className="ml-auto text-fg-dim">
                        {themePickerOpen ? '▾' : '▸'}
                      </span>
                    </button>
                    {themePickerOpen && (
                      <div className="panel border-t border-line animate-fade-in sm:absolute sm:right-full sm:top-0 sm:mr-0 sm:w-56 sm:border-t-0 z-40">
                        <div className="px-3 py-1.5 border-b border-line uppercase tracking-[0.08em] text-[11px] text-fg-dim">
                          pick a theme
                        </div>
                        {THEMES.map((t, i) => {
                          const active = t.id === theme
                          const prev = i > 0 ? THEMES[i - 1] : null
                          const groupBreak = prev && prev.mode !== t.mode
                          return (
                            <button
                              key={t.id}
                              onClick={() => {
                                setTheme(t.id)
                                setThemePickerOpen(false)
                              }}
                              className={`w-full text-left px-3 py-1.5 text-[13.5px] hover:bg-surface transition-colors flex items-center gap-2 ${
                                groupBreak ? 'border-t border-line' : ''
                              } ${active ? 'text-fg' : 'text-fg-soft hover:text-fg'}`}
                            >
                              <span className={active ? 'text-accent' : 'text-fg-dim'}>
                                {active ? '●' : '○'}
                              </span>
                              <span>{t.label}</span>
                              <span className="ml-auto text-fg-dim text-[11px] uppercase tracking-[0.08em]">
                                {t.mode}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                  {menuExtras && (
                    <div
                      className="border-t border-line"
                      onClick={() => setMenuOpen(false)}
                    >
                      {menuExtras}
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setMenuOpen(false)
                      logout()
                    }}
                    className="w-full text-left px-3 py-2 text-[14px] text-danger hover:bg-danger hover:text-bg border-t border-line transition-colors"
                    aria-label="Log out"
                  >
                    <span className="mr-2">&gt;</span>
                    logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {bureau && (
          <div className="min-[750px]:hidden px-3 pb-2 flex items-center gap-2 overflow-x-auto">
            <BureauChip host={bureau.host} role={bureau.role} onClick={onOpenRolodex} />
          </div>
        )}
      </header>

      <main className="flex-1 min-h-0 flex flex-col overflow-hidden">{children}</main>

      <footer className="border-t border-line bg-bg-raise px-3 py-1.5 shrink-0">
        <div className="flex items-center justify-between gap-4 font-mono text-[11.5px] text-fg-dim tnum">
          <span className="inline-flex items-center gap-2">
            <StatusDot tone="accent" pulse size={6} />
            <span>connected</span>
          </span>
          <span className="hidden min-[1200px]:inline">
            ai operator console · {new Date().toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' })}
          </span>
          <span>v0.1.0</span>
        </div>
      </footer>
    </div>
  )
}
